import * as React from "react";
import { useDispatch } from "react-redux";
import clsx from "classnames";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import Paper from "@material-ui/core/Paper";
import { ListItem, ListItemSecondaryAction, ListItemText, List as MuiList } from "@material-ui/core";

//icons
// import DownloadIcon from "mdi-material-ui/Download";

import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

// jaza-soft
import { WithReduxForm, Button, showNotification } from "jazasoft";

// local
import CardHeader from "../../../components/CardHeader";
import MuiTable from "../../../components/MuiTable";

import useMutation from "../../../hooks/useMutation";

import { useDeepMemo, useEntityPrivilege } from "../../../hooks";
import { isEmpty, isTrue } from "../../../utils/helpers";
import { LedgerType } from "../../../utils/types";
import { colorTomatoRed } from "../../../utils/colors";
import { ResourceId, Scope } from "../../../utils/privilege";
import hasPrivilege from "../../../utils/hasPrivilege";

const titleMap = {
  sample_issue: "Sample Issue",
  outsource: "Outsource",
  return_supplier: "Return Supplier",
  write_off: "Write Off"
};

const useStyles = makeStyles(theme => ({
  root: {
    width: 680,
    display: "flex",
    flexDirection: "column",
    height: "100vh"
  },
  drawerPaper: {
    maxWidth: 1000
  },
  drawerHeader: {
    display: "flex",
    width: 680,
    alignItems: "center",
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  content: {
    display: "flex",
    flex: 1,
    flexDirection: "column"
  },
  footer: {
    display: "flex",
    padding: "1.5em"
  },
  padding: {
    padding: "1.5em"
  },
  marginVertical: {
    marginTop: "1.5em"
  }
}));

const RollViewDrawer = props => {
  const classes = useStyles();
  const { open, rollInfoList, ledger, onDrawerClose, roles, hasAccess, onRefresh } = props;

  const { disableDelete } = useEntityPrivilege("MaterialOut");
  const hasMaterialOutPrivilege = hasPrivilege(roles, hasAccess, ResourceId.MATERIAL_OUT, Scope.DELETE);

  const [mutate] = useMutation();
  const dispatch = useDispatch();

  const action =
    ledger?.outType === "Sampling"
      ? "sample_issue"
      : ledger?.outType === "Outsource"
      ? "outsource"
      : ledger?.outType === "Return-Supplier"
      ? "return_supplier"
      : ledger?.outType === "Write-Off"
      ? "write_off"
      : "";

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "rollNo", title: "Factory Roll No" },
      { dataKey: "supplierRollNo", title: "Supplier Roll No" },
      { dataKey: "length", title: "Length" },
      { dataKey: "invStatus", title: "Inv. Status", filterOptions: { filter: true } },
      { dataKey: "qcStatus", title: "QC Status", filterOptions: { filter: true } }
    ];
    return cols;
  }, [rollInfoList]);

  const onDelete = () => {
    const options = {
      url: `material-out`,
      method: "delete",
      params: { txn: ledger.txn, type: action }
    };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification(`Material Out (${titleMap[action]}) deleted successfully.`));
        onRefresh && onRefresh();
        onDrawerClose && onDrawerClose();
      }
    });
  };

  const rows = useDeepMemo(() => {
    if (isEmpty(rollInfoList) || isEmpty(ledger)) return [];
    let rollList = [];
    if (ledger.type === LedgerType.IN) {
      rollList = rollInfoList.filter(ri => ri.ledgerIn?.id === ledger.id);
    } else if (ledger.type === LedgerType.ITEM_TRANSFER) {
      const factoryRolls = ledger.rollList?.map(e => e.factoryRollNo) || [];
      rollList = rollInfoList.filter(ri => factoryRolls.includes(ri.rollNo));
    } else {
      rollList = rollInfoList.filter(ri => ri.ledgerOut?.id === ledger.id);
    }
    // rollList = rollList.map(ri => ({ ...ri, invStatus: isTrue(ri.active) ? ri.invStatus : "Transferred" }));
    rollList = rollList.filter(ri => isTrue(ri.active));
    return rollList;
  }, [rollInfoList, ledger]);

  const listData = useDeepMemo(() => {
    if (isEmpty(ledger)) return [];
    if (ledger.type === LedgerType.OUT && ledger.outType !== "Bulk") {
      let listData = [];
      if (ledger.outType === "Sampling") {
        listData.push({ label: "Sample Type", value: ledger.sampleType });
      } else {
        listData.push({ label: "Challan No", value: ledger.challanNo });
        listData.push({ label: "Out Destination", value: ledger.outDestination });
      }
      listData.push({ label: "Remarks", value: ledger.remarks });
      return listData;
    }
    return [];
  }, [ledger]);

  return (
    <div>
      <Drawer anchor="right" classes={{ paper: classes.drawerPaper }} open={open} onClose={onDrawerClose}>
        <WithReduxForm>
          {() => (
            <div className={classes.root}>
              <div className={classes.drawerHeader}>
                <IconButton onClick={onDrawerClose}>
                  <ChevronRightIcon />
                </IconButton>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", width: "100%", paddingRight: "1.5em" }}>
                  <Typography variant="h5">Rolls: TXN = {ledger?.txn || ""}</Typography>
                </div>
              </div>
              <Divider />

              <div className={clsx(classes.content, { [classes.padding]: true })}>
                <Paper elevation={1}>
                  <MuiList disablePadding>
                    {listData.map(({ label, value }, idx) => (
                      <ListItem key={idx} divider>
                        <ListItemText primary={label} />
                        <ListItemSecondaryAction>
                          <ListItemText primary={value} />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </MuiList>
                </Paper>

                <div className={clsx({ [classes.marginVertical]: !isEmpty(listData) })}>
                  <MuiTable
                    title={<CardHeader title={"Rolls"} style={{ paddingLeft: 0 }} />}
                    columns={columns}
                    rows={rows}
                    pageable
                    searchable
                    searchKeys={["rollNo", "factoryRollNo"]}
                    toolbar
                  />
                </div>
              </div>

              {!isEmpty(action) && hasMaterialOutPrivilege && !disableDelete && (
                <div className={classes.footer}>
                  <Button
                    label="Delete"
                    variant="outlined"
                    style={{ color: colorTomatoRed.dark, borderColor: colorTomatoRed.dark }}
                    onClick={onDelete}
                  />
                </div>
              )}
            </div>
          )}
        </WithReduxForm>
      </Drawer>
    </div>
  );
};

export default RollViewDrawer;
